import type { LabTurn, LabTurnDiff } from "@/lib/labModel";
import type { LabRunModel } from "@/lib/labRunModel";
import {
  SAFETY_INTERNALS_FIELD,
  describeSafetyDetection,
  describeSafetyIntervention,
  describeSafetyMechanism,
  describeSafetyReason,
  describeWithheld,
  safetyOutcomeLabel,
  safetyOutcomeTone,
} from "@/lib/previewDecisions";
import { Badge } from "@/ui/Badge";

import { NOTHING, Section } from "./LabTurnSummary";

/**
 * Safety tab: each mechanism's report with the policy that governed it. The
 * guard's internals are restricted technical content, withheld at source: they
 * are stated as withheld (with the digest and size the producer carried), and
 * no body is ever read or shown.
 */
export function LabSafetyTab({
  turn,
  diff = null,
}: {
  projectId: string;
  previewRunRef: string;
  turn: LabTurn;
  model: LabRunModel;
  onSelectTurn: (turn: number) => void;
  diff?: LabTurnDiff | null;
}) {
  return (
    <Section title="Safety" differs={diff?.safety === true}>
      {turn.safety.length === 0 ? NOTHING : null}
      {turn.safety.map((event) => {
        const { detail } = event;
        const internals = detail.guardInternals;
        const facts = [
          internals.contentHash,
          internals.byteSize !== null ? `${internals.byteSize} bytes` : null,
        ].filter((x): x is string => x !== null);
        return (
          <div
            key={event.eventRef}
            className="space-y-1 border-b border-border pb-2 text-sm last:border-b-0"
          >
            <p className="flex flex-wrap items-center gap-2">
              <span dir="auto" className="font-medium">
                {describeSafetyMechanism(detail.mechanism) ?? detail.mechanism}
              </span>
              <Badge tone={safetyOutcomeTone(detail.outcome)}>
                {safetyOutcomeLabel(detail.outcome)}
              </Badge>
            </p>
            <p>{describeSafetyReason(detail.reasonCode)}</p>
            <p className="text-muted-foreground">
              {describeSafetyDetection(detail.detection)}{" "}
              {describeSafetyIntervention(detail.intervention)}
            </p>
            <p dir="auto" className="whitespace-pre-wrap break-words">
              {detail.clinicalEffect}
            </p>
            <p className="text-muted-foreground">
              Policy:{" "}
              <span dir="ltr">
                {`${detail.policyIdentity.id} v${detail.policyIdentity.version}`}
              </span>
            </p>
            <p className="text-muted-foreground">
              Guard internals (
              <code className="font-mono text-xs">
                {SAFETY_INTERNALS_FIELD}
              </code>
              ): withheld by contract
              {facts.length > 0 ? (
                <>
                  {" "}
                  <span dir="ltr" className="break-all text-xs">
                    ({facts.join(", ")})
                  </span>
                </>
              ) : null}
            </p>
            {describeWithheld(internals) !== null ? (
              <p className="text-xs text-muted-foreground">
                {describeWithheld(internals)}
              </p>
            ) : null}
          </div>
        );
      })}
    </Section>
  );
}
